/**
 * ClashPlacementModal - Cribbage Clash Interactive Board Placement System
 * Encapsulates the visual SVG placement modal, candidate hit targeting,
 * jump vector arrows, conflict-aware placement, option range updating,
 * and intelligent full/partial clash randomizers.
 */

import { BOARD_CFG, getClashBoardCfg, getTrackXY } from '../board/BoardGeometry.js';
import { getClashDefaults } from '../board/ObstacleGeometry.js';
import { ADULT_OPTIONS_LIST } from './AdultSettings.js';
import { ClashPresets } from './ClashPresets.js';

export function getJumpTriggerLength(jumpIdx, overrideStart = null) {
  if (typeof document === 'undefined') return 1;
  const dEl = document.getElementById(`optJump${jumpIdx}Dest`);
  const dVal = dEl ? parseInt(dEl.value, 10) : 0;
  let sVal = overrideStart;
  if (sVal == null) {
    const sEl = document.getElementById(`optJump${jumpIdx}Start`);
    sVal = sEl ? parseInt(sEl.value, 10) : 0;
  }
  if (!isNaN(sVal) && !isNaN(dVal) && dVal > 0 && sVal > 0) {
    return dVal > sVal ? 1 : 3;
  }
  return 1;
}

export function buildClashItemMetadata(settings = null) {
  const meta = {
    optNoPassingLane1Start: { name: 'No Passing Lane 1', type: 'noPassingLanes', icon: '🚫', color: '#ef4444', getLength: () => 5 },
    optNoPassingLane2Start: { name: 'No Passing Lane 2', type: 'noPassingLanes', icon: '🚫', color: '#ef4444', getLength: () => 5 },
    optNoPassingLane3Start: { name: 'No Passing Lane 3', type: 'noPassingLanes', icon: '🚫', color: '#ef4444', getLength: () => 5 },
    optNoPassingLane4Start: { name: 'No Passing Lane 4', type: 'noPassingLanes', icon: '🚫', color: '#ef4444', getLength: () => 5 },

    optJump1Start: { name: 'Jump 1 Trigger', type: 'jump', icon: '⚡', color: '#3b82f6', getLength: (h) => getJumpTriggerLength(1, h) },
    optJump1Dest: { name: 'Jump 1 Destination', type: 'jumpDest', icon: '🎯', color: '#0ea5e9', getLength: () => 1 },
    optJump2Start: { name: 'Jump 2 Trigger', type: 'jump', icon: '⚡', color: '#3b82f6', getLength: (h) => getJumpTriggerLength(2, h) },
    optJump2Dest: { name: 'Jump 2 Destination', type: 'jumpDest', icon: '🎯', color: '#0ea5e9', getLength: () => 1 },
    optJump3Start: { name: 'Jump 3 Trigger', type: 'jump', icon: '⚡', color: '#3b82f6', getLength: (h) => getJumpTriggerLength(3, h) },
    optJump3Dest: { name: 'Jump 3 Destination', type: 'jumpDest', icon: '🎯', color: '#0ea5e9', getLength: () => 1 },
    optJump4Start: { name: 'Jump 4 Trigger', type: 'jump', icon: '⚡', color: '#3b82f6', getLength: (h) => getJumpTriggerLength(4, h) },
    optJump4Dest: { name: 'Jump 4 Destination', type: 'jumpDest', icon: '🎯', color: '#0ea5e9', getLength: () => 1 },
    optJump5Start: { name: 'Jump 5 Trigger', type: 'jump', icon: '⚡', color: '#3b82f6', getLength: (h) => getJumpTriggerLength(5, h) },
    optJump5Dest: { name: 'Jump 5 Destination', type: 'jumpDest', icon: '🎯', color: '#0ea5e9', getLength: () => 1 },
    optJump6Start: { name: 'Jump 6 Trigger', type: 'jump', icon: '⚡', color: '#3b82f6', getLength: (h) => getJumpTriggerLength(6, h) },
    optJump6Dest: { name: 'Jump 6 Destination', type: 'jumpDest', icon: '🎯', color: '#0ea5e9', getLength: () => 1 },
    optJump7Start: { name: 'Jump 7 Trigger', type: 'jump', icon: '⚡', color: '#3b82f6', getLength: (h) => getJumpTriggerLength(7, h) },
    optJump7Dest: { name: 'Jump 7 Destination', type: 'jumpDest', icon: '🎯', color: '#0ea5e9', getLength: () => 1 },
    optJump8Start: { name: 'Jump 8 Trigger', type: 'jump', icon: '⚡', color: '#3b82f6', getLength: (h) => getJumpTriggerLength(8, h) },
    optJump8Dest: { name: 'Jump 8 Destination', type: 'jumpDest', icon: '🎯', color: '#0ea5e9', getLength: () => 1 },

    optJail1Hole: { name: 'Jail 1', type: 'jail', icon: '🔒', color: '#94a3b8', getLength: () => 1 },
    optJail2Hole: { name: 'Jail 2', type: 'jail', icon: '🔒', color: '#94a3b8', getLength: () => 1 },

    optBar1Hole: { name: 'The Bar 1', type: 'bar', icon: '🍸', color: '#f59e0b', getLength: () => 1 },
    optBar2Hole: { name: 'The Bar 2', type: 'bar', icon: '🍸', color: '#f59e0b', getLength: () => 1 },

    optTimeout1Hole: { name: 'Timeout 1', type: 'timeout', icon: '⏳', color: '#8b5cf6', getLength: () => 1 },
    optTimeout2Hole: { name: 'Timeout 2', type: 'timeout', icon: '⏳', color: '#8b5cf6', getLength: () => 1 },

    optDetour1Hole: { name: 'Detour 1', type: 'detour', icon: '🔀', color: '#06b6d4', getLength: () => 1 },
    optDetour2Hole: { name: 'Detour 2', type: 'detour', icon: '🔀', color: '#06b6d4', getLength: () => 1 },

    optRoundAbout1Hole: { name: 'Round About 1', type: 'roundAbout', icon: '🔄', color: '#10b981', getLength: () => 1 },
    optRoundAbout2Hole: { name: 'Round About 2', type: 'roundAbout', icon: '🔄', color: '#10b981', getLength: () => 1 },

    optSigns1Hole: { name: 'Sign Block 1', type: 'signs', icon: '🪧', color: '#ec4899', getLength: () => { const sz = typeof document !== 'undefined' ? document.getElementById('optSigns1Size') : null; return (sz && parseInt(sz.value, 10) === 4) ? 5 : 3; } },
    optSigns2Hole: { name: 'Sign Block 2', type: 'signs', icon: '🪧', color: '#ec4899', getLength: () => { const sz = typeof document !== 'undefined' ? document.getElementById('optSigns2Size') : null; return (sz && parseInt(sz.value, 10) === 4) ? 5 : 3; } },
    optSigns3Hole: { name: 'Sign Block 3', type: 'signs', icon: '🪧', color: '#ec4899', getLength: () => { const sz = typeof document !== 'undefined' ? document.getElementById('optSigns3Size') : null; return (sz && parseInt(sz.value, 10) === 4) ? 5 : 3; } },
    optSigns4Hole: { name: 'Sign Block 4', type: 'signs', icon: '🪧', color: '#ec4899', getLength: () => { const sz = typeof document !== 'undefined' ? document.getElementById('optSigns4Size') : null; return (sz && parseInt(sz.value, 10) === 4) ? 5 : 3; } },

    optStopSign1Hole: { name: 'Stop Sign 1', type: 'stopSign', icon: '🛑', color: '#ef4444', getLength: () => 1 },
    optStopSign2Hole: { name: 'Stop Sign 2', type: 'stopSign', icon: '🛑', color: '#ef4444', getLength: () => 1 },
    optStopSign3Hole: { name: 'Stop Sign 3', type: 'stopSign', icon: '🛑', color: '#ef4444', getLength: () => 1 },
    optStopSign4Hole: { name: 'Stop Sign 4', type: 'stopSign', icon: '🛑', color: '#ef4444', getLength: () => 1 },
    optStopSign5Hole: { name: 'Stop Sign 5', type: 'stopSign', icon: '🛑', color: '#ef4444', getLength: () => 1 },
    optStopSign6Hole: { name: 'Stop Sign 6', type: 'stopSign', icon: '🛑', color: '#ef4444', getLength: () => 1 },
    optStopSign7Hole: { name: 'Stop Sign 7', type: 'stopSign', icon: '🛑', color: '#ef4444', getLength: () => 1 },
    optStopSign8Hole: { name: 'Stop Sign 8', type: 'stopSign', icon: '🛑', color: '#ef4444', getLength: () => 1 },

    optDice1Hole: { name: 'Dice 1', type: 'dice', icon: '🎲', color: '#a855f7', getLength: () => 1 },
    optDice2Hole: { name: 'Dice 2', type: 'dice', icon: '🎲', color: '#a855f7', getLength: () => 1 },
    optDice3Hole: { name: 'Dice 3', type: 'dice', icon: '🎲', color: '#a855f7', getLength: () => 1 },
    optDice4Hole: { name: 'Dice 4', type: 'dice', icon: '🎲', color: '#a855f7', getLength: () => 1 },

    optWheel1Hole: { name: 'Spinning Wheel 1', type: 'spinningWheel', icon: '🎡', color: '#ec4899', getLength: () => 1 },
    optWheel2Hole: { name: 'Spinning Wheel 2', type: 'spinningWheel', icon: '🎡', color: '#ec4899', getLength: () => 1 },
    optWheel3Hole: { name: 'Spinning Wheel 3', type: 'spinningWheel', icon: '🎡', color: '#ec4899', getLength: () => 1 },
    optWheel4Hole: { name: 'Spinning Wheel 4', type: 'spinningWheel', icon: '🎡', color: '#ec4899', getLength: () => 1 },

    optSkunkMarkerHole: { name: 'Skunk Marker', type: 'skunkMarker', icon: '🦨', color: '#64748b', getLength: () => 1, allowsOverlap: true },
    optDoubleSkunkMarkerHole: { name: 'Double Skunk Marker', type: 'doubleSkunkMarker', icon: '🦨', color: '#475569', getLength: () => 1, allowsOverlap: true },

    optYouDrink1Hole: { name: 'You Drink 1', type: 'youDrink', icon: '🍺', color: '#f59e0b', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optYouDrinkPegs')?.value) || (settings && settings.youDrinkPegs) || 1, 10)) },
    optYouDrink2Hole: { name: 'You Drink 2', type: 'youDrink', icon: '🍺', color: '#f59e0b', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optYouDrinkPegs')?.value) || (settings && settings.youDrinkPegs) || 1, 10)) },
    optYouDrink3Hole: { name: 'You Drink 3', type: 'youDrink', icon: '🍺', color: '#f59e0b', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optYouDrinkPegs')?.value) || (settings && settings.youDrinkPegs) || 1, 10)) },
    optYouDrink4Hole: { name: 'You Drink 4', type: 'youDrink', icon: '🍺', color: '#f59e0b', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optYouDrinkPegs')?.value) || (settings && settings.youDrinkPegs) || 1, 10)) },

    optGuysDrink1Hole: { name: 'Guys Drink 1', type: 'guysDrink', icon: '🍻', color: '#06b6d4', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optGuysDrinkPegs')?.value) || (settings && settings.guysDrinkPegs) || 1, 10)) },
    optGuysDrink2Hole: { name: 'Guys Drink 2', type: 'guysDrink', icon: '🍻', color: '#06b6d4', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optGuysDrinkPegs')?.value) || (settings && settings.guysDrinkPegs) || 1, 10)) },
    optGuysDrink3Hole: { name: 'Guys Drink 3', type: 'guysDrink', icon: '🍻', color: '#06b6d4', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optGuysDrinkPegs')?.value) || (settings && settings.guysDrinkPegs) || 1, 10)) },
    optGuysDrink4Hole: { name: 'Guys Drink 4', type: 'guysDrink', icon: '🍻', color: '#06b6d4', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optGuysDrinkPegs')?.value) || (settings && settings.guysDrinkPegs) || 1, 10)) },

    optGalsDrink1Hole: { name: 'Gals Drink 1', type: 'galsDrink', icon: '🍷', color: '#ec4899', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optGalsDrinkPegs')?.value) || (settings && settings.galsDrinkPegs) || 1, 10)) },
    optGalsDrink2Hole: { name: 'Gals Drink 2', type: 'galsDrink', icon: '🍷', color: '#ec4899', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optGalsDrinkPegs')?.value) || (settings && settings.galsDrinkPegs) || 1, 10)) },
    optGalsDrink3Hole: { name: 'Gals Drink 3', type: 'galsDrink', icon: '🍷', color: '#ec4899', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optGalsDrinkPegs')?.value) || (settings && settings.galsDrinkPegs) || 1, 10)) },
    optGalsDrink4Hole: { name: 'Gals Drink 4', type: 'galsDrink', icon: '🍷', color: '#ec4899', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optGalsDrinkPegs')?.value) || (settings && settings.galsDrinkPegs) || 1, 10)) },

    optLeftDrink1Hole: { name: 'Left Drinks 1', type: 'leftDrink', icon: '👈', color: '#8b5cf6', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optLeftDrinkPegs')?.value) || (settings && settings.leftDrinkPegs) || 1, 10)) },
    optLeftDrink2Hole: { name: 'Left Drinks 2', type: 'leftDrink', icon: '👈', color: '#8b5cf6', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optLeftDrinkPegs')?.value) || (settings && settings.leftDrinkPegs) || 1, 10)) },
    optLeftDrink3Hole: { name: 'Left Drinks 3', type: 'leftDrink', icon: '👈', color: '#8b5cf6', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optLeftDrinkPegs')?.value) || (settings && settings.leftDrinkPegs) || 1, 10)) },
    optLeftDrink4Hole: { name: 'Left Drinks 4', type: 'leftDrink', icon: '👈', color: '#8b5cf6', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optLeftDrinkPegs')?.value) || (settings && settings.leftDrinkPegs) || 1, 10)) },

    optRightDrink1Hole: { name: 'Right Drinks 1', type: 'rightDrink', icon: '👉', color: '#3b82f6', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optRightDrinkPegs')?.value) || (settings && settings.rightDrinkPegs) || 1, 10)) },
    optRightDrink2Hole: { name: 'Right Drinks 2', type: 'rightDrink', icon: '👉', color: '#3b82f6', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optRightDrinkPegs')?.value) || (settings && settings.rightDrinkPegs) || 1, 10)) },
    optRightDrink3Hole: { name: 'Right Drinks 3', type: 'rightDrink', icon: '👉', color: '#3b82f6', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optRightDrinkPegs')?.value) || (settings && settings.rightDrinkPegs) || 1, 10)) },
    optRightDrink4Hole: { name: 'Right Drinks 4', type: 'rightDrink', icon: '👉', color: '#3b82f6', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optRightDrinkPegs')?.value) || (settings && settings.rightDrinkPegs) || 1, 10)) },

    optEveryoneDrink1Hole: { name: 'Everyone Drinks 1', type: 'everyoneDrink', icon: '🥂', color: '#10b981', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optEveryoneDrinkPegs')?.value) || (settings && settings.everyoneDrinkPegs) || 1, 10)) },
    optEveryoneDrink2Hole: { name: 'Everyone Drinks 2', type: 'everyoneDrink', icon: '🥂', color: '#10b981', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optEveryoneDrinkPegs')?.value) || (settings && settings.everyoneDrinkPegs) || 1, 10)) },
    optEveryoneDrink3Hole: { name: 'Everyone Drinks 3', type: 'everyoneDrink', icon: '🥂', color: '#10b981', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optEveryoneDrinkPegs')?.value) || (settings && settings.everyoneDrinkPegs) || 1, 10)) },
    optEveryoneDrink4Hole: { name: 'Everyone Drinks 4', type: 'everyoneDrink', icon: '🥂', color: '#10b981', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optEveryoneDrinkPegs')?.value) || (settings && settings.everyoneDrinkPegs) || 1, 10)) },

    optEveryoneButYouDrink1Hole: { name: 'Everyone But You 1', type: 'everyoneButYouDrink', icon: '🎉', color: '#f97316', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optEveryoneButYouDrinkPegs')?.value) || (settings && settings.everyoneButYouDrinkPegs) || 1, 10)) },
    optEveryoneButYouDrink2Hole: { name: 'Everyone But You 2', type: 'everyoneButYouDrink', icon: '🎉', color: '#f97316', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optEveryoneButYouDrinkPegs')?.value) || (settings && settings.everyoneButYouDrinkPegs) || 1, 10)) },
    optEveryoneButYouDrink3Hole: { name: 'Everyone But You 3', type: 'everyoneButYouDrink', icon: '🎉', color: '#f97316', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optEveryoneButYouDrinkPegs')?.value) || (settings && settings.everyoneButYouDrinkPegs) || 1, 10)) },
    optEveryoneButYouDrink4Hole: { name: 'Everyone But You 4', type: 'everyoneButYouDrink', icon: '🎉', color: '#f97316', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optEveryoneButYouDrinkPegs')?.value) || (settings && settings.everyoneButYouDrinkPegs) || 1, 10)) },

    optPickWhoDrinks1Hole: { name: 'Pick Who Drinks 1', type: 'pickWhoDrinks', icon: '🎯', color: '#e11d48', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optPickWhoDrinksPegs')?.value) || (settings && settings.pickWhoDrinksPegs) || 1, 10)) },
    optPickWhoDrinks2Hole: { name: 'Pick Who Drinks 2', type: 'pickWhoDrinks', icon: '🎯', color: '#e11d48', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optPickWhoDrinksPegs')?.value) || (settings && settings.pickWhoDrinksPegs) || 1, 10)) },
    optPickWhoDrinks3Hole: { name: 'Pick Who Drinks 3', type: 'pickWhoDrinks', icon: '🎯', color: '#e11d48', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optPickWhoDrinksPegs')?.value) || (settings && settings.pickWhoDrinksPegs) || 1, 10)) },
    optPickWhoDrinks4Hole: { name: 'Pick Who Drinks 4', type: 'pickWhoDrinks', icon: '🎯', color: '#e11d48', getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById('optPickWhoDrinksPegs')?.value) || (settings && settings.pickWhoDrinksPegs) || 1, 10)) }
  };

  if (typeof ADULT_OPTIONS_LIST !== 'undefined' && Array.isArray(ADULT_OPTIONS_LIST)) {
    ADULT_OPTIONS_LIST.forEach(opt => {
      for (let i = 1; i <= 4; i++) {
        const id = `${opt.prefix}${i}Hole`;
        meta[id] = {
          name: `${opt.name} ${i}`,
          type: opt.key,
          icon: opt.icon,
          color: opt.color,
          getLength: () => (parseInt((typeof document !== 'undefined' && document.getElementById(opt.pegsId)?.value) || (settings && settings[opt.key + 'Pegs']) || 1, 10))
        };
      }
    });
  }

  return meta;
}

export class ClashPlacementModal {
  static currentSelectId = null;
  static currentSelectedHole = null;
  static metadata = null;

  static getMetadata(settings = null) {
    if (!ClashPlacementModal.metadata) {
      ClashPlacementModal.metadata = buildClashItemMetadata(settings);
    }
    return ClashPlacementModal.metadata;
  }

  static initPlacementButtons(meta = null) {
    if (typeof document === 'undefined') return;
    const itemMeta = meta || ClashPlacementModal.getMetadata();

    for (const selectId of Object.keys(itemMeta)) {
      const sel = document.getElementById(selectId);
      if (!sel) continue;

      sel.classList.add('clash-select-hidden');

      let btn = document.getElementById(`btnPlace_${selectId}`);
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.id = `btnPlace_${selectId}`;
        btn.className = 'clash-place-btn';
        btn.onclick = () => ClashPlacementModal.open(selectId);
        sel.parentNode.insertBefore(btn, sel.nextSibling);
      }

      sel.addEventListener('change', () => {
        sel.dataset.customized = 'true';
        ClashPlacementModal.syncPlacementButton(selectId, itemMeta);
      });
    }
    ClashPlacementModal.syncAllPlacementButtons(itemMeta);
  }

  static syncPlacementButton(selectId, meta = null) {
    if (typeof document === 'undefined') return;
    const itemMeta = meta || ClashPlacementModal.getMetadata();
    const sel = document.getElementById(selectId);
    const btn = document.getElementById(`btnPlace_${selectId}`);
    const item = itemMeta[selectId];
    if (!sel || !btn || !item) return;

    let val = parseInt(sel.value, 10);
    if (isNaN(val) && sel.options && sel.options.length > 0) {
      val = parseInt(sel.options[0].value, 10);
      sel.value = String(val);
    }
    if (isNaN(val)) return;
    const len = typeof item.getLength === 'function' ? item.getLength(val) : 1;
    let label = `Hole ${val}`;
    if (len > 1) {
      label = `Holes ${val}–${val + len - 1}`;
    }

    btn.innerHTML = `
      <span>${item.icon}</span>
      <span class="clash-place-val">${label}</span>
      <span class="clash-place-action">Choose ➔</span>
    `;
  }

  static syncAllPlacementButtons(meta = null) {
    if (typeof document === 'undefined') return;
    const itemMeta = meta || ClashPlacementModal.getMetadata();
    for (const selectId of Object.keys(itemMeta)) {
      ClashPlacementModal.syncPlacementButton(selectId, itemMeta);
    }
  }

  static getAllPlacedItems(excludeSelectId = null, meta = null) {
    if (typeof document === 'undefined') return [];
    const itemMeta = meta || ClashPlacementModal.getMetadata();
    const items = [];

    for (const [id, m] of Object.entries(itemMeta)) {
      if (id === excludeSelectId) continue;
      const sel = document.getElementById(id);
      if (!sel) continue;

      let isActive = true;
      if (id.startsWith('optNoPassingLane')) {
        const chk = document.getElementById('optNoPassingLanes');
        const cnt = parseInt(document.getElementById('optNoPassingLaneCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optNoPassingLane', '').replace('Start', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optJump')) {
        const chk = document.getElementById('optJump');
        const cnt = parseInt(document.getElementById('optJumpCount')?.value, 10) || 1;
        const match = id.match(/\d+/);
        const idx = match ? parseInt(match[0], 10) : 1;
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optJail')) {
        const chk = document.getElementById('optJail');
        const cnt = parseInt(document.getElementById('optJailCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optJail', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optBar')) {
        const chk = document.getElementById('optBar');
        const cnt = parseInt(document.getElementById('optBarCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optBar', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optTimeout')) {
        const chk = document.getElementById('optTimeout');
        const cnt = parseInt(document.getElementById('optTimeoutCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optTimeout', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optDetour')) {
        const chk = document.getElementById('optDetour');
        const cnt = parseInt(document.getElementById('optDetourCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optDetour', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optRoundAbout')) {
        const chk = document.getElementById('optRoundAbout');
        const cnt = parseInt(document.getElementById('optRoundAboutCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optRoundAbout', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optSigns')) {
        const chk = document.getElementById('optSigns');
        const cnt = parseInt(document.getElementById('optSignsCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optSigns', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optStopSign')) {
        const chk = document.getElementById('optStopSign');
        const cnt = parseInt(document.getElementById('optStopSignCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optStopSign', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optDice')) {
        const chk = document.getElementById('optDice');
        const cnt = parseInt(document.getElementById('optDiceCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optDice', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optWheel')) {
        const chk = document.getElementById('optWheel');
        const cnt = parseInt(document.getElementById('optWheelCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optWheel', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id === 'optSkunkMarkerHole' || id === 'optDoubleSkunkMarkerHole') {
        const chk = document.getElementById('optSkunkMarkers');
        isActive = chk && chk.checked;
      } else if (id.startsWith('optYouDrink')) {
        const chk = document.getElementById('optYouDrink');
        const cnt = parseInt(document.getElementById('optYouDrinkCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optYouDrink', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optGuysDrink')) {
        const chk = document.getElementById('optGuysDrink');
        const cnt = parseInt(document.getElementById('optGuysDrinkCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optGuysDrink', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optGalsDrink')) {
        const chk = document.getElementById('optGalsDrink');
        const cnt = parseInt(document.getElementById('optGalsDrinkCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optGalsDrink', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optLeftDrink')) {
        const chk = document.getElementById('optLeftDrink');
        const cnt = parseInt(document.getElementById('optLeftDrinkCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optLeftDrink', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optRightDrink')) {
        const chk = document.getElementById('optRightDrink');
        const cnt = parseInt(document.getElementById('optRightDrinkCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optRightDrink', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optEveryoneDrink')) {
        const chk = document.getElementById('optEveryoneDrink');
        const cnt = parseInt(document.getElementById('optEveryoneDrinkCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optEveryoneDrink', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optEveryoneButYouDrink')) {
        const chk = document.getElementById('optEveryoneButYouDrink');
        const cnt = parseInt(document.getElementById('optEveryoneButYouDrinkCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optEveryoneButYouDrink', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      } else if (id.startsWith('optPickWhoDrinks')) {
        const chk = document.getElementById('optPickWhoDrinks');
        const cnt = parseInt(document.getElementById('optPickWhoDrinksCount')?.value, 10) || 1;
        const idx = parseInt(id.replace('optPickWhoDrinks', '').replace('Hole', ''), 10);
        isActive = chk && chk.checked && idx <= cnt;
      }

      if (!isActive && typeof ADULT_OPTIONS_LIST !== 'undefined' && Array.isArray(ADULT_OPTIONS_LIST)) {
        const adultOpt = ADULT_OPTIONS_LIST.find(o => id.startsWith(o.prefix));
        if (adultOpt) {
          const chk = document.getElementById(adultOpt.chkId);
          const cnt = parseInt(document.getElementById(adultOpt.countId)?.value, 10) || 1;
          const idx = parseInt(id.replace(adultOpt.prefix, '').replace('Hole', ''), 10);
          isActive = chk && chk.checked && idx <= cnt;
        }
      }

      if (!isActive) continue;
      const val = parseInt(sel.value, 10);
      if (isNaN(val)) continue;
      const len = typeof m.getLength === 'function' ? m.getLength(val) : 1;
      items.push({
        selectId: id,
        name: m.name,
        icon: m.icon,
        color: m.color,
        type: m.type,
        start: val,
        end: val + len - 1,
        length: len,
        allowsOverlap: Boolean(m.allowsOverlap)
      });
    }
    return items;
  }

  static open(selectId, options = {}) {
    if (typeof document === 'undefined') return;
    const itemMeta = options.meta || ClashPlacementModal.getMetadata();
    const meta = itemMeta[selectId];
    const sel = document.getElementById(selectId);
    if (!meta || !sel) return;

    ClashPlacementModal.currentSelectId = selectId;
    ClashPlacementModal.currentSelectedHole = parseInt(sel.value, 10) || 10;

    const overlay = document.getElementById('clashPlacementOverlay');
    if (!overlay) return;

    const iconEl = document.getElementById('clashModalIcon');
    const titleEl = document.getElementById('clashModalTitle');
    const subEl = document.getElementById('clashModalSub');
    const len = typeof meta.getLength === 'function' ? meta.getLength(ClashPlacementModal.currentSelectedHole) : 1;

    if (iconEl) iconEl.textContent = meta.icon;
    if (titleEl) titleEl.textContent = `Choose Placement: ${meta.name}`;
    if (subEl) {
      if (meta.allowsOverlap) {
        subEl.textContent = 'Milestone marker. Can be placed anywhere on the track, including over other options.';
      } else if (selectId.startsWith('optJump') && selectId.endsWith('Start')) {
        subEl.textContent = 'Forward jumps occupy 1 hole; backward jumps span 3 holes. Click any available group on the track. Occupied locations are locked.';
      } else {
        subEl.textContent = `Occupies ${len} hole${len > 1 ? 's' : ''}. Click any available group of holes on the track. Occupied locations are locked.`;
      }
    }

    overlay.style.display = 'flex';
    ClashPlacementModal.renderPlacementBoard(selectId, options);
  }

  static close() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('clashPlacementOverlay');
    if (overlay) overlay.style.display = 'none';
    ClashPlacementModal.currentSelectId = null;
    ClashPlacementModal.currentSelectedHole = null;
  }

  static onConfirm(options = {}) {
    if (!ClashPlacementModal.currentSelectId || ClashPlacementModal.currentSelectedHole == null) return;
    const sel = document.getElementById(ClashPlacementModal.currentSelectId);
    const selectId = ClashPlacementModal.currentSelectId;
    const selectedHole = ClashPlacementModal.currentSelectedHole;

    if (sel) {
      sel.dataset.customized = 'true';
      sel.value = String(selectedHole);
      if (typeof sel.dispatchEvent === 'function' && typeof Event !== 'undefined') {
        sel.dispatchEvent(new Event('change'));
      }

      // Trigger individual change handlers if appropriate
      if (typeof window !== 'undefined') {
        if (selectId.startsWith('optJail')) {
          const idx = parseInt(selectId.replace('optJail', '').replace('Hole', ''), 10);
          if (typeof window.onJailFieldChange === 'function') window.onJailFieldChange(idx);
        } else if (selectId.startsWith('optBar')) {
          const idx = parseInt(selectId.replace('optBar', '').replace('Hole', ''), 10);
          if (typeof window.onBarFieldChange === 'function') window.onBarFieldChange(idx);
        } else if (selectId.startsWith('optTimeout')) {
          const idx = parseInt(selectId.replace('optTimeout', '').replace('Hole', ''), 10);
          if (typeof window.onTimeoutFieldChange === 'function') window.onTimeoutFieldChange(idx);
        } else if (selectId.startsWith('optDetour')) {
          const idx = parseInt(selectId.replace('optDetour', '').replace('Hole', ''), 10);
          if (typeof window.onDetourFieldChange === 'function') window.onDetourFieldChange(idx);
        } else if (selectId.startsWith('optRoundAbout')) {
          const idx = parseInt(selectId.replace('optRoundAbout', '').replace('Hole', ''), 10);
          if (typeof window.onRoundAboutFieldChange === 'function') window.onRoundAboutFieldChange(idx);
        } else if (selectId.startsWith('optSigns')) {
          const idx = parseInt(selectId.replace('optSigns', '').replace('Hole', ''), 10);
          if (typeof window.onSignsFieldChange === 'function') window.onSignsFieldChange(idx);
        } else if (selectId.startsWith('optNoPassingLane')) {
          const idx = parseInt(selectId.replace('optNoPassingLane', '').replace('Start', ''), 10);
          if (typeof window.onNoPassingLaneFieldChange === 'function') window.onNoPassingLaneFieldChange(idx);
        } else if (selectId.startsWith('optJump')) {
          const m = selectId.match(/\d+/);
          const idx = m ? parseInt(m[0], 10) : 1;
          if (typeof window.onJumpFieldChange === 'function') window.onJumpFieldChange(idx);
        } else if (selectId.startsWith('optStopSign')) {
          const idx = parseInt(selectId.replace('optStopSign', '').replace('Hole', ''), 10);
          if (typeof window.onStopSignFieldChange === 'function') window.onStopSignFieldChange(idx);
        } else if (selectId.startsWith('optDice')) {
          const idx = parseInt(selectId.replace('optDice', '').replace('Hole', ''), 10);
          if (typeof window.onDiceFieldChange === 'function') window.onDiceFieldChange(idx);
        } else if (selectId.startsWith('optWheel')) {
          const idx = parseInt(selectId.replace('optWheel', '').replace('Hole', ''), 10);
          if (typeof window.onWheelFieldChange === 'function') window.onWheelFieldChange(idx);
        } else if (selectId === 'optSkunkMarkerHole') {
          if (typeof window.onSkunkMarkerFieldChange === 'function') window.onSkunkMarkerFieldChange();
        } else if (selectId === 'optDoubleSkunkMarkerHole') {
          if (typeof window.onDoubleSkunkMarkerFieldChange === 'function') window.onDoubleSkunkMarkerFieldChange();
        } else if (selectId.startsWith('optYouDrink')) {
          const idx = parseInt(selectId.replace('optYouDrink', '').replace('Hole', ''), 10);
          if (typeof window.onYouDrinkFieldChange === 'function') window.onYouDrinkFieldChange(idx);
        } else if (selectId.startsWith('optGuysDrink')) {
          const idx = parseInt(selectId.replace('optGuysDrink', '').replace('Hole', ''), 10);
          if (typeof window.onGuysDrinkFieldChange === 'function') window.onGuysDrinkFieldChange(idx);
        } else if (selectId.startsWith('optGalsDrink')) {
          const idx = parseInt(selectId.replace('optGalsDrink', '').replace('Hole', ''), 10);
          if (typeof window.onGalsDrinkFieldChange === 'function') window.onGalsDrinkFieldChange(idx);
        } else if (selectId.startsWith('optLeftDrink')) {
          const idx = parseInt(selectId.replace('optLeftDrink', '').replace('Hole', ''), 10);
          if (typeof window.onLeftDrinkFieldChange === 'function') window.onLeftDrinkFieldChange(idx);
        } else if (selectId.startsWith('optRightDrink')) {
          const idx = parseInt(selectId.replace('optRightDrink', '').replace('Hole', ''), 10);
          if (typeof window.onRightDrinkFieldChange === 'function') window.onRightDrinkFieldChange(idx);
        } else if (selectId.startsWith('optEveryoneDrink')) {
          const idx = parseInt(selectId.replace('optEveryoneDrink', '').replace('Hole', ''), 10);
          if (typeof window.onEveryoneDrinkFieldChange === 'function') window.onEveryoneDrinkFieldChange(idx);
        } else if (selectId.startsWith('optEveryoneButYouDrink')) {
          const idx = parseInt(selectId.replace('optEveryoneButYouDrink', '').replace('Hole', ''), 10);
          if (typeof window.onEveryoneButYouDrinkFieldChange === 'function') window.onEveryoneButYouDrinkFieldChange(idx);
        } else if (selectId.startsWith('optPickWhoDrinks')) {
          const idx = parseInt(selectId.replace('optPickWhoDrinks', '').replace('Hole', ''), 10);
          if (typeof window.onPickWhoDrinksFieldChange === 'function') window.onPickWhoDrinksFieldChange(idx);
        } else if (typeof ADULT_OPTIONS_LIST !== 'undefined' && ADULT_OPTIONS_LIST.some(o => selectId.startsWith(o.prefix))) {
          const adultOpt = ADULT_OPTIONS_LIST.find(o => selectId.startsWith(o.prefix));
          const idx = parseInt(selectId.replace(adultOpt.prefix, '').replace('Hole', ''), 10);
          if (typeof window.onAdultOptionFieldChange === 'function') window.onAdultOptionFieldChange(adultOpt.key, idx);
        }

        if (typeof window.enforceClashNoOverlap === 'function') {
          window.enforceClashNoOverlap(selectId);
        }
      }
    }

    ClashPlacementModal.syncAllPlacementButtons();
    const meta = ClashPlacementModal.getMetadata()[selectId];
    ClashPlacementModal.close();

    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(`✓ Placed ${meta ? meta.name : 'Option'} at Hole ${selectedHole}`);
    }
  }

  static renderPlacementBoard(selectId, options = {}) {
    if (typeof document === 'undefined') return;
    const container = document.getElementById('clashBoardSvgWrap');
    if (!container) return;
    container.innerHTML = '';

    const itemMeta = options.meta || ClashPlacementModal.getMetadata();
    const meta = itemMeta[selectId] || { name: 'Option', icon: '📍', color: '#f0c64a', getLength: () => 1 };
    function getItemLength(startCandidate = null) {
      if (typeof meta.getLength === 'function') {
        return meta.getLength(startCandidate);
      }
      return 1;
    }

    const isPintsItem = selectId.includes('Drink');
    const isAdultItem = typeof ADULT_OPTIONS_LIST !== 'undefined' && ADULT_OPTIONS_LIST.some(o => selectId.startsWith(o.prefix));
    const targetVal = (document.getElementById('optTarget') ? parseInt(document.getElementById('optTarget').value, 10) : (options.settings && options.settings.target)) || 481;
    const isClashTarget = [241, 361, 481].includes(targetVal);
    const boardTarget = (isPintsItem || isAdultItem) ? 121 : (isClashTarget ? targetVal : 121);
    const isClashBoard = [241, 361, 481].includes(boardTarget);
    if (typeof window !== 'undefined') window._clashBoardTarget = boardTarget;

    let viewW, viewH, targetHoles;
    let clashCfg = null;
    if (isClashBoard) {
      clashCfg = getClashBoardCfg(boardTarget);
      viewW = clashCfg.viewW;
      viewH = clashCfg.viewH;
      targetHoles = boardTarget - 1;
    } else {
      viewW = BOARD_CFG.viewW;
      viewH = BOARD_CFG.viewH;
      targetHoles = 120;
    }

    const placedItems = ClashPlacementModal.getAllPlacedItems(selectId, itemMeta);

    // Update placed badges list in legend
    const badgesWrap = document.getElementById('clashPlacedBadges');
    if (badgesWrap) {
      badgesWrap.innerHTML = placedItems.length ? placedItems.map(b => `
        <span class="cpl-placed-tag" style="border-color: ${b.color}; color: ${b.color};">
          ${b.icon} ${b.name}: ${b.start}${b.length > 1 ? '–' + b.end : ''}
        </span>
      `).join('') : '<span style="color: #64748b;">No other options active</span>';
    }

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${viewW} ${viewH}`);

    const bgX = 15;
    const bgY = 12;
    const bgW = viewW - 30;
    const bgH = viewH - 24;
    const bgRx = 16;

    const svgContent = `
      <defs>
        <radialGradient id="plHoleGrad" cx="38%" cy="38%" r="65%">
          <stop offset="0%" stop-color="#020101" />
          <stop offset="65%" stop-color="#120803" />
          <stop offset="100%" stop-color="#241005" />
        </radialGradient>
        <linearGradient id="plBoardWoodGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="#eed5ad" />
          <stop offset="45%" stop-color="#e2c191" />
          <stop offset="80%" stop-color="#d4af7a" />
          <stop offset="100%" stop-color="#c69e67" />
        </linearGradient>
        <filter id="plGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="0" stdDeviation="2.5" flood-color="#f0c64a" flood-opacity="0.9" />
        </filter>
        <marker id="plJumpArrowGreen" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#22c55e" />
        </marker>
        <marker id="plJumpArrowRed" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#ef4444" />
        </marker>
      </defs>
      <rect x="${bgX}" y="${bgY}" width="${bgW}" height="${bgH}" rx="${bgRx}"
            fill="url(#plBoardWoodGrad)" stroke="#6b3a12" stroke-width="3" />
      <rect x="${bgX + 2}" y="${bgY + 2}" width="${bgW - 4}" height="${bgH - 4}" rx="${Math.max(2, bgRx - 2)}"
            fill="none" stroke="rgba(255, 240, 215, 0.45)" stroke-width="1.2" />
      <g id="plMilestonesGroup"></g>
      <g id="plJumpsLayer"></g>
      <g id="plHolesGroup"></g>
      <g id="plPlacedOverlaysGroup"></g>
      <g id="plHitAreasGroup"></g>
      <g id="plLabelsGroup"></g>
    `;
    svg.innerHTML = svgContent;
    container.appendChild(svg);

    const milestonesGroup = svg.querySelector('#plMilestonesGroup');
    const jumpsGroup = svg.querySelector('#plJumpsLayer');
    const holesGroup = svg.querySelector('#plHolesGroup');
    const overlaysGroup = svg.querySelector('#plPlacedOverlaysGroup');
    const hitAreasGroup = svg.querySelector('#plHitAreasGroup');
    const labelsGroup = svg.querySelector('#plLabelsGroup');

    // Milestone tick lines
    if (isClashBoard && clashCfg) {
      for (let r = 0; r < clashCfg.numRows; r++) {
        const y = clashCfg.stY[r];
        const startH = r * 60 + 1;
        for (let g = 1; g <= 11; g++) {
          const h = startH + (g * 5) - 1;
          const pt = getTrackXY(h, 'p1', boardTarget);
          const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          line.setAttribute('x1', pt.x);
          line.setAttribute('y1', y - 9);
          line.setAttribute('x2', pt.x);
          line.setAttribute('y2', y + 9);
          line.setAttribute('stroke', '#6d421a');
          line.setAttribute('stroke-opacity', '0.65');
          line.setAttribute('stroke-width', '1.2');
          milestonesGroup.appendChild(line);
        }
        const stTxt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        stTxt.setAttribute('x', r % 2 === 0 ? 32 : 788);
        stTxt.setAttribute('y', y + 3.5);
        stTxt.setAttribute('fill', '#5c3814');
        stTxt.setAttribute('font-size', '8.5px');
        stTxt.setAttribute('font-weight', '700');
        stTxt.setAttribute('text-anchor', 'middle');
        stTxt.textContent = `ST ${r + 1}`;
        labelsGroup.appendChild(stTxt);
      }
    } else {
      const streets = [
        { num: 1, y: BOARD_CFG.st1Y, start: 5, end: 30, xLabel: 32 },
        { num: 2, y: BOARD_CFG.st2Y, start: 40, end: 65, xLabel: 788 },
        { num: 3, y: BOARD_CFG.st3Y, start: 75, end: 120, xLabel: 32 }
      ];
      streets.forEach(st => {
        for (let h = st.start; h <= st.end; h += 5) {
          const pt = getTrackXY(h, 'p1', boardTarget);
          const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          line.setAttribute('x1', pt.x);
          line.setAttribute('y1', st.y - 9);
          line.setAttribute('x2', pt.x);
          line.setAttribute('y2', st.y + 9);
          line.setAttribute('stroke', '#6d421a');
          line.setAttribute('stroke-opacity', '0.65');
          line.setAttribute('stroke-width', '1.2');
          milestonesGroup.appendChild(line);
        }
        const stTxt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        stTxt.setAttribute('x', st.xLabel);
        stTxt.setAttribute('y', st.y + 3.5);
        stTxt.setAttribute('fill', '#5c3814');
        stTxt.setAttribute('font-size', '8.5px');
        stTxt.setAttribute('font-weight', '700');
        stTxt.setAttribute('text-anchor', 'middle');
        stTxt.textContent = `ST ${st.num}`;
        labelsGroup.appendChild(stTxt);
      });
    }

    // Draw Holes
    const holeCircleMap = new Map();
    for (let h = 1; h <= targetHoles; h++) {
      const pt = getTrackXY(h, 'p1', boardTarget);
      const isMilestone = h > 0 && h % 5 === 0;
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', pt.x);
      circle.setAttribute('cy', pt.y);
      circle.setAttribute('r', isMilestone ? '2.8' : '2');
      circle.setAttribute('fill', 'url(#plHoleGrad)');
      circle.setAttribute('stroke', isMilestone ? '#3a1b07' : '#1a0b03');
      circle.setAttribute('stroke-width', isMilestone ? '1.2' : '0.8');
      circle.setAttribute('id', `plHoleCircle_${h}`);
      holesGroup.appendChild(circle);
      holeCircleMap.set(h, circle);
    }

    // Render Already Placed Obstacles
    placedItems.forEach(item => {
      for (let h = item.start; h <= item.end; h++) {
        const c = holeCircleMap.get(h);
        if (c) {
          c.setAttribute('fill', item.color);
          c.setAttribute('stroke', '#ffffff');
          c.setAttribute('stroke-width', '1.2');
        }
      }
      const midHole = Math.round((item.start + item.end) / 2);
      const midPt = getTrackXY(midHole, 'p1', boardTarget);
      const pin = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      pin.setAttribute('x', midPt.x);
      pin.setAttribute('y', midPt.y - 10);
      pin.setAttribute('class', 'placement-pin');
      pin.textContent = item.icon;
      const pinTitle = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      pinTitle.textContent = `${item.name} (Holes ${item.start}–${item.end})`;
      pin.appendChild(pinTitle);
      overlaysGroup.appendChild(pin);
    });

    // Calculate Candidate Groups
    const step = 5;
    const startHole = (selectId.includes('Jump') || selectId.includes('StopSign') || selectId.includes('Skunk') || isPintsItem || isAdultItem) ? 5 : 10;
    const maxClashHole = isClashBoard ? (boardTarget - 11) : 110;
    const maxStopSignHole = isClashBoard ? (boardTarget - 6) : 115;
    const maxJumpDestHole = isClashBoard ? (boardTarget - 1) : 120;
    const maxMarkerHole = isClashBoard ? (boardTarget - 1) : 120;
    const endHole = (isPintsItem || isAdultItem)
      ? Math.max(5, 120 - getItemLength(120) + 1)
      : (isClashBoard
        ? (selectId.includes('Skunk') ? maxMarkerHole : (selectId.includes('JumpDest') ? maxJumpDestHole : (selectId.includes('StopSign') ? maxStopSignHole : maxClashHole)))
        : (selectId.includes('Skunk') ? 120 : (selectId.includes('JumpDest') ? 120 : (selectId.includes('StopSign') ? 115 : (selectId.includes('NoPassing') ? 100 : 110)))));

    function drawJumpArrows(previewHole = null) {
      if (!jumpsGroup) return;
      jumpsGroup.innerHTML = '';

      const chk = document.getElementById('optJump');
      const isEditingJump = selectId.startsWith('optJump');
      const isJumpActive = (chk && chk.checked) || isEditingJump;
      if (!isJumpActive) return;

      const count = parseInt(document.getElementById('optJumpCount')?.value, 10) || 2;
      const currentEditedIdx = selectId.startsWith('optJump') ? parseInt(selectId.match(/\d+/)?.[0] || '1', 10) : null;
      const maxJump = Math.max(count, currentEditedIdx || 1);

      for (let i = 1; i <= maxJump; i++) {
        let sVal = parseInt(document.getElementById(`optJump${i}Start`)?.value, 10);
        let dVal = parseInt(document.getElementById(`optJump${i}Dest`)?.value, 10);

        const targetHole = (previewHole != null) ? previewHole : ClashPlacementModal.currentSelectedHole;

        if (selectId === `optJump${i}Start` && targetHole != null) {
          sVal = targetHole;
        } else if (selectId === `optJump${i}Dest` && targetHole != null) {
          dVal = targetHole;
        }

        if (isNaN(sVal) || isNaN(dVal) || sVal <= 0 || dVal <= 0 || sVal === dVal) continue;
        if (sVal > targetHoles || dVal > targetHoles) continue;

        const isForward = dVal > sVal;
        const sHole = Math.min(targetHoles, isForward ? sVal : sVal + 1);
        const dHole = dVal;

        const pStart = getTrackXY(sHole, 'p1', boardTarget);
        const pEnd = getTrackXY(dHole, 'p1', boardTarget);

        const dx = pEnd.x - pStart.x;
        const dy = pEnd.y - pStart.y;
        const dist = Math.hypot(dx, dy);
        if (dist < 4) continue;

        const mx = (pStart.x + pEnd.x) / 2;
        const my = (pStart.y + pEnd.y) / 2;
        const nx = -dy / dist;
        const ny = dx / dist;

        let arcSign = -1;
        if (Math.abs(dy) < 15) {
          arcSign = my > (viewH / 2) ? 1 : -1;
        } else {
          arcSign = isForward ? -1 : 1;
        }
        const curveHeight = Math.max(16, Math.min(50, dist * 0.25)) * arcSign;
        const cx = mx + nx * curveHeight;
        const cy = my + ny * curveHeight;

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', `M ${pStart.x} ${pStart.y} Q ${cx} ${cy} ${pEnd.x} ${pEnd.y}`);
        path.setAttribute('class', isForward ? 'jump-arrow-forward' : 'jump-arrow-backward');
        path.setAttribute('marker-end', isForward ? 'url(#plJumpArrowGreen)' : 'url(#plJumpArrowRed)');

        const diff = Math.abs(dVal - sVal);
        const jumpSpanText = isForward ? `Hole ${sVal}` : `Holes ${sVal}–${sVal + 2}`;
        const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
        title.textContent = `Jump ${i}: ${jumpSpanText} ➔ Hole ${dVal} (${isForward ? `Forward +${diff}` : `Backward -${diff}`})`;
        path.appendChild(title);

        jumpsGroup.appendChild(path);
      }
    }

    function updateHoverHighlight(start, isHover) {
      const len = getItemLength(start);
      for (let h = start; h < start + len; h++) {
        const c = holeCircleMap.get(h);
        if (c) {
          if (isHover) {
            c.classList.add('placement-hole-hover-span');
          } else {
            c.classList.remove('placement-hole-hover-span');
          }
        }
      }
      if (selectId.startsWith('optJump')) {
        drawJumpArrows(isHover ? start : null);
      }
    }

    function setSelectedHole(h) {
      ClashPlacementModal.currentSelectedHole = h;
      const confirmBtn = document.getElementById('btnConfirmClashPlacement');
      if (confirmBtn) confirmBtn.disabled = false;

      const len = getItemLength(h);
      const rangeText = len > 1 ? `Holes ${h}–${h + len - 1}` : `Hole ${h}`;
      const street = (typeof window !== 'undefined' && typeof window.getStreetOrTurnName === 'function') ? window.getStreetOrTurnName(h) : '';
      let jumpDetails = '';
      if (selectId.startsWith('optJump')) {
        const jIdx = selectId.match(/\d+/)?.[0] || '1';
        if (selectId.endsWith('Start')) {
          const dVal = parseInt(document.getElementById(`optJump${jIdx}Dest`)?.value, 10);
          if (!isNaN(dVal)) {
            const isFwd = dVal > h;
            const diff = Math.abs(dVal - h);
            jumpDetails = ` · <span style="color: ${isFwd ? '#22c55e' : '#ef4444'}; font-weight: 800;">${isFwd ? '➔ Forward Jump (+' + diff + ')' : '➔ Backward Jump (-' + diff + ')'}</span> to Hole ${dVal}`;
          }
        } else if (selectId.endsWith('Dest')) {
          const sVal = parseInt(document.getElementById(`optJump${jIdx}Start`)?.value, 10);
          if (!isNaN(sVal)) {
            const isFwd = h > sVal;
            const diff = Math.abs(h - sVal);
            const fromText = isFwd ? `Hole ${sVal}` : `Holes ${sVal}–${sVal + 2}`;
            jumpDetails = ` · <span style="color: ${isFwd ? '#22c55e' : '#ef4444'}; font-weight: 800;">${isFwd ? '➔ Forward Jump (+' + diff + ')' : '➔ Backward Jump (-' + diff + ')'}</span> from ${fromText}`;
          }
        }
      }

      const statusVal = document.getElementById('clashStatusSelected');
      if (statusVal) {
        statusVal.innerHTML = `<span style="color: ${meta.color}; font-weight: 700;">${meta.icon} ${meta.name}:</span> ${street ? street + ' · ' : ''}${rangeText}${jumpDetails}`;
      }

      // Clear previous candidate active circles
      svg.querySelectorAll('.candidate-hole-selected').forEach(el => el.remove());

      // Add selected badge/halo around the holes
      for (let cur = h; cur < h + len; cur++) {
        const pt = getTrackXY(cur, 'p1', boardTarget);
        const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        ring.setAttribute('cx', pt.x);
        ring.setAttribute('cy', pt.y);
        ring.setAttribute('r', '6');
        ring.setAttribute('fill', 'none');
        ring.setAttribute('stroke', '#f0c64a');
        ring.setAttribute('stroke-width', '2');
        ring.setAttribute('class', 'candidate-hole-selected');
        ring.setAttribute('filter', 'url(#plGlow)');
        holesGroup.appendChild(ring);
      }

      drawJumpArrows(h);
    }

    // Draw Hit Areas for every candidate multiple of 5
    for (let s = startHole; s <= endHole; s += step) {
      const len = getItemLength(s);
      if (s + len - 1 > targetHoles) continue;
      let overlaps = false;
      if (!meta.allowsOverlap) {
        for (const it of placedItems) {
          if (it.allowsOverlap) continue;
          if (s <= it.end && (s + len - 1) >= it.start) {
            overlaps = true;
            break;
          }
        }
      }

      const pStart = getTrackXY(s, 'p1', boardTarget);
      const pEnd = getTrackXY(s + len - 1, 'p1', boardTarget);
      const midX = (pStart.x + pEnd.x) / 2;
      const midY = (pStart.y + pEnd.y) / 2;

      const hit = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      hit.setAttribute('cx', midX);
      hit.setAttribute('cy', midY);
      hit.setAttribute('r', 5);
      hit.setAttribute('class', `placement-candidate-hit ${overlaps ? 'is-occupied' : 'is-available'}`);
      hit.setAttribute('data-hole', s);

      const rangeText = len > 1 ? `Holes ${s}–${s + len - 1}` : `Hole ${s}`;
      const tip = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      tip.textContent = overlaps ? `${rangeText} (Overlaps other placed option)` : `${rangeText} (Click to place)`;
      hit.appendChild(tip);

      if (!overlaps) {
        hit.addEventListener('mouseenter', () => {
          updateHoverHighlight(s, true);
          const statusVal = document.getElementById('clashStatusSelected');
          if (statusVal && ClashPlacementModal.currentSelectedHole !== s) {
            const street = (typeof window !== 'undefined' && typeof window.getStreetOrTurnName === 'function') ? window.getStreetOrTurnName(s) : '';
            let hoverJumpExtra = '';
            if (selectId.startsWith('optJump')) {
              const jIdx = selectId.match(/\d+/)?.[0] || '1';
              if (selectId.endsWith('Start')) {
                const dVal = parseInt(document.getElementById(`optJump${jIdx}Dest`)?.value, 10);
                if (!isNaN(dVal)) {
                  const isFwd = dVal > s;
                  const diff = Math.abs(dVal - s);
                  hoverJumpExtra = ` · <span style="color: ${isFwd ? '#22c55e' : '#ef4444'}; font-weight: 800;">${isFwd ? '➔ Forward Jump (+' + diff + ')' : '➔ Backward Jump (-' + diff + ')'}</span> to Hole ${dVal}`;
                }
              } else if (selectId.endsWith('Dest')) {
                const sVal = parseInt(document.getElementById(`optJump${jIdx}Start`)?.value, 10);
                if (!isNaN(sVal)) {
                  const isFwd = s > sVal;
                  const diff = Math.abs(s - sVal);
                  const fromText = isFwd ? `Hole ${sVal}` : `Holes ${sVal}–${sVal + 2}`;
                  hoverJumpExtra = ` · <span style="color: ${isFwd ? '#22c55e' : '#ef4444'}; font-weight: 800;">${isFwd ? '➔ Forward Jump (+' + diff + ')' : '➔ Backward Jump (-' + diff + ')'}</span> from ${fromText}`;
                }
              }
            }
            statusVal.innerHTML = `<span style="color: #34d399;">Hovering:</span> ${street ? street + ' · ' : ''}${rangeText}${hoverJumpExtra} (Click to place)`;
          }
        });

        hit.addEventListener('mouseleave', () => {
          updateHoverHighlight(s, false);
          if (ClashPlacementModal.currentSelectedHole != null) {
            setSelectedHole(ClashPlacementModal.currentSelectedHole);
          }
        });

        hit.addEventListener('click', () => {
          setSelectedHole(s);
        });
      }

      hitAreasGroup.appendChild(hit);
    }

    // Pre-select current value and render initial arrows
    drawJumpArrows();
    if (ClashPlacementModal.currentSelectedHole != null && ClashPlacementModal.currentSelectedHole >= startHole && ClashPlacementModal.currentSelectedHole <= endHole) {
      setSelectedHole(ClashPlacementModal.currentSelectedHole);
    } else {
      const confirmBtn = document.getElementById('btnConfirmClashPlacement');
      if (confirmBtn) confirmBtn.disabled = true;
      const statusVal = document.getElementById('clashStatusSelected');
      if (statusVal) statusVal.textContent = 'None selected — Click any green group on the track';
    }
  }

  static updateOptionRanges(targetOrIsClash, options = {}) {
    if (typeof document === 'undefined') return;

    function rebuildOptions(selectId, items, defaultValue) {
      const sel = document.getElementById(selectId);
      if (!sel) return;
      const currentVal = parseInt(sel.value, 10);
      const lastOpt = sel.options[sel.options.length - 1];
      const isMatchingOptions = lastOpt && parseInt(lastOpt.value, 10) === items[items.length - 1].value && sel.options.length === items.length;
      if (!isMatchingOptions) {
        sel.innerHTML = '';
        items.forEach(item => {
          const opt = document.createElement('option');
          opt.value = String(item.value);
          opt.textContent = item.label;
          sel.appendChild(opt);
        });
      }
      const isCustom = sel.dataset.customized === 'true';
      let targetVal = (isCustom && !isNaN(currentVal) && items.some(it => it.value === currentVal))
        ? currentVal
        : defaultValue;
      if (isNaN(targetVal)) targetVal = (items[0] && items[0].value != null) ? items[0].value : 5;
      if (!items.some(it => it.value === targetVal) && !isNaN(targetVal)) {
        const opt = document.createElement('option');
        opt.value = String(targetVal);
        opt.textContent = `Hole ${targetVal}`;
        sel.appendChild(opt);
      }
      sel.value = String(targetVal);
      delete sel.dataset.originalText;
    }

    let clashTarget = 481;
    if (typeof targetOrIsClash === 'number') {
      clashTarget = targetOrIsClash;
    } else if (targetOrIsClash === false) {
      clashTarget = 121;
    } else {
      const optTarget = document.getElementById('optTarget');
      if (optTarget && [241, 361, 481].includes(parseInt(optTarget.value, 10))) {
        clashTarget = parseInt(optTarget.value, 10);
      } else if (options.settings && [241, 361, 481].includes(options.settings.target)) {
        clashTarget = options.settings.target;
      }
    }
    const isClashTarget = [241, 361, 481].includes(clashTarget);
    const defs = isClashTarget ? getClashDefaults(clashTarget) : null;
    const maxSingleHole = isClashTarget ? (clashTarget - 11) : 110;

    // 1. Single-hole traps
    const singleHoleItems = [];
    for (let v = 10; v <= maxSingleHole; v += 5) {
      singleHoleItems.push({ value: v, label: `Hole ${v}` });
    }

    rebuildOptions('optJail1Hole', singleHoleItems, defs ? defs.jailHoles[0] : 25);
    rebuildOptions('optJail2Hole', singleHoleItems, defs ? defs.jailHoles[1] : 75);

    rebuildOptions('optBar1Hole', singleHoleItems, defs ? defs.barHoles[0] : 40);
    rebuildOptions('optBar2Hole', singleHoleItems, defs ? defs.barHoles[1] : 85);

    rebuildOptions('optTimeout1Hole', singleHoleItems, defs ? defs.timeoutHoles[0] : 30);
    rebuildOptions('optTimeout2Hole', singleHoleItems, defs ? defs.timeoutHoles[1] : 90);

    rebuildOptions('optDetour1Hole', singleHoleItems, defs ? defs.detourHoles[0] : 10);
    rebuildOptions('optDetour2Hole', singleHoleItems, defs ? defs.detourHoles[1] : 60);

    rebuildOptions('optRoundAbout1Hole', singleHoleItems, defs ? defs.roundAboutHoles[0] : 35);
    rebuildOptions('optRoundAbout2Hole', singleHoleItems, defs ? defs.roundAboutHoles[1] : 70);

    rebuildOptions('optSigns1Hole', singleHoleItems, defs ? defs.signsStarts[0] : 45);
    rebuildOptions('optSigns2Hole', singleHoleItems, defs ? defs.signsStarts[1] : 55);
    rebuildOptions('optSigns3Hole', singleHoleItems, defs ? defs.signsStarts[2] : 95);
    rebuildOptions('optSigns4Hole', singleHoleItems, defs ? defs.signsStarts[3] : 105);

    const defaultDice121 = [20, 50, 80, 100];
    const defaultDiceClash = defs ? defs.diceHoles : null;
    for (let i = 1; i <= 4; i++) {
      const defVal = defaultDiceClash ? defaultDiceClash[i - 1] : defaultDice121[i - 1];
      rebuildOptions(`optDice${i}Hole`, singleHoleItems, defVal);
    }

    const defaultWheel121 = [30, 60, 90, 110];
    const defaultWheelClash = defs ? defs.wheelHoles : null;
    for (let i = 1; i <= 4; i++) {
      const defVal = defaultWheelClash ? defaultWheelClash[i - 1] : defaultWheel121[i - 1];
      rebuildOptions(`optWheel${i}Hole`, singleHoleItems, defVal);
    }

    // 2. Stop Signs
    const maxStopSignHole = isClashTarget ? (clashTarget - 6) : 115;
    const stopSignItems = [];
    for (let v = 5; v <= maxStopSignHole; v += 5) {
      stopSignItems.push({ value: v, label: `Hole ${v}` });
    }
    const defaultStops121 = [20, 35, 50, 65, 80, 95, 105, 115];
    const defaultStopsClash = defs ? defs.stopSignHoles : null;
    for (let i = 1; i <= 8; i++) {
      const defVal = defaultStopsClash ? defaultStopsClash[i - 1] : defaultStops121[i - 1];
      rebuildOptions(`optStopSign${i}Hole`, stopSignItems, defVal);
    }

    // 3. No Passing Lanes (multiples of 10)
    const maxNplHole = isClashTarget ? (clashTarget - 11) : 100;
    const nplItems = [];
    for (let v = 10; v <= maxNplHole; v += 10) {
      nplItems.push({ value: v, label: `Holes ${v}–${v + 4}` });
    }
    const defaultNpl121 = [20, 50, 80, 100];
    const defaultNplClash = defs ? defs.noPassingLaneStarts : null;
    for (let i = 1; i <= 4; i++) {
      const defVal = defaultNplClash ? defaultNplClash[i - 1] : defaultNpl121[i - 1];
      rebuildOptions(`optNoPassingLane${i}Start`, nplItems, defVal);
    }

    // 4. Jumps (multiples of 5)
    const maxJumpStartHole = isClashTarget ? (clashTarget - 11) : 115;
    const maxJumpDestHole = isClashTarget ? (clashTarget - 1) : 120;
    const jumpStartItems = [];
    for (let v = 5; v <= maxJumpStartHole; v += 5) {
      jumpStartItems.push({ value: v, label: `Hole ${v}` });
    }
    const jumpDestItems = [];
    for (let v = 5; v <= maxJumpDestHole; v += 5) {
      jumpDestItems.push({ value: v, label: `Hole ${v}` });
    }
    const defaultJumpStarts121 = [15, 65, 5, 115, 25, 75, 45, 95];
    const defaultJumpDests121 = [35, 50, 45, 70, 55, 60, 80, 85];
    const defaultJumpStartsClash = defs ? defs.jumpStarts : null;
    const defaultJumpDestsClash = defs ? defs.jumpDests : null;
    for (let i = 1; i <= 8; i++) {
      const defStart = defaultJumpStartsClash ? defaultJumpStartsClash[i - 1] : defaultJumpStarts121[i - 1];
      const defDest = defaultJumpDestsClash ? defaultJumpDestsClash[i - 1] : defaultJumpDests121[i - 1];
      rebuildOptions(`optJump${i}Start`, jumpStartItems, defStart);
      rebuildOptions(`optJump${i}Dest`, jumpDestItems, defDest);
    }

    ClashPlacementModal.syncAllPlacementButtons();
  }

  static randomizeOptions(isFull = false, options = {}) {
    if (typeof window !== 'undefined') window._isRandomizingClash = true;
    try {
      const targetVal = (document.getElementById('optTarget') ? parseInt(document.getElementById('optTarget').value, 10) : (options.settings && options.settings.target)) || 481;
      const isClashTarget = [241, 361, 481].includes(targetVal);
      const clashTarget = isClashTarget ? targetVal : 481;
      const maxClashHole = isClashTarget ? (clashTarget - 11) : 110;
      const jailSel = document.getElementById('optJail1Hole');
      const needsRanges = !jailSel || !jailSel.options || jailSel.options.length === 0 ||
        jailSel.options[jailSel.options.length - 1].value !== String(maxClashHole);
      if (needsRanges) {
        ClashPlacementModal.updateOptionRanges(clashTarget, options);
      }

      // Track occupied hole sets
      const occupiedHoles = new Set();
      function isFree(start, length) {
        for (let h = start; h < start + length; h++) {
          if (occupiedHoles.has(h)) return false;
        }
        return true;
      }
      function occupy(start, length) {
        for (let h = start; h < start + length; h++) {
          occupiedHoles.add(h);
        }
      }
      function shuffle(arr) {
        const a = [...arr];
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      }
      function getMultiples(step, max, min = 10) {
        const out = [];
        for (let v = min; v <= max; v += step) out.push(v);
        return out;
      }

      // Counts
      let signsCount, nplCount, jumpCount, jailCount, barCount, timeoutCount, detourCount, roundAboutCount, stopSignCount;
      if (isFull) {
        signsCount = 4; nplCount = 4; jumpCount = 8;
        jailCount = 2; barCount = 2; timeoutCount = 2; detourCount = 2; roundAboutCount = 2;
        stopSignCount = 8;
      } else {
        signsCount = Math.floor(Math.random() * 4) + 1;
        nplCount = Math.floor(Math.random() * 4) + 1;
        jumpCount = Math.floor(Math.random() * 8) + 1;
        jailCount = Math.floor(Math.random() * 2) + 1;
        barCount = Math.floor(Math.random() * 2) + 1;
        timeoutCount = Math.floor(Math.random() * 2) + 1;
        detourCount = Math.floor(Math.random() * 2) + 1;
        roundAboutCount = Math.floor(Math.random() * 2) + 1;
        stopSignCount = Math.floor(Math.random() * 8) + 1;
      }

      function setChecked(id, val) {
        const el = document.getElementById(id);
        if (el) el.checked = val;
      }
      function setCount(id, val) {
        const el = document.getElementById(id);
        if (el) el.value = String(val);
      }

      setChecked('optSigns', true); setCount('optSignsCount', signsCount);
      setChecked('optNoPassingLanes', true); setCount('optNoPassingLaneCount', nplCount);
      setChecked('optJump', true); setCount('optJumpCount', jumpCount);
      setChecked('optJail', true); setCount('optJailCount', jailCount);
      setChecked('optBar', true); setCount('optBarCount', barCount);
      setChecked('optTimeout', true); setCount('optTimeoutCount', timeoutCount);
      setChecked('optDetour', true); setCount('optDetourCount', detourCount);
      setChecked('optRoundAbout', true); setCount('optRoundAboutCount', roundAboutCount);
      setChecked('optStopSign', true); setCount('optStopSignCount', stopSignCount);
      const diceCount = isFull ? 4 : (Math.floor(Math.random() * 3) + 2); // 2..4
      setChecked('optDice', true); setCount('optDiceCount', diceCount);
      const wheelCount = isFull ? 4 : (Math.floor(Math.random() * 3) + 2); // 2..4
      setChecked('optWheel', true); setCount('optWheelCount', wheelCount);

      if (typeof window !== 'undefined') {
        if (typeof window.toggleSignsConfig === 'function') window.toggleSignsConfig(true);
        if (typeof window.toggleNoPassingLanesConfig === 'function') window.toggleNoPassingLanesConfig(true);
        if (typeof window.toggleJumpConfig === 'function') window.toggleJumpConfig(true);
        if (typeof window.toggleJailConfig === 'function') window.toggleJailConfig(true);
        if (typeof window.toggleBarConfig === 'function') window.toggleBarConfig(true);
        if (typeof window.toggleTimeoutConfig === 'function') window.toggleTimeoutConfig(true);
        if (typeof window.toggleDetourConfig === 'function') window.toggleDetourConfig(true);
        if (typeof window.toggleRoundAboutConfig === 'function') window.toggleRoundAboutConfig(true);
        if (typeof window.toggleStopSignConfig === 'function') window.toggleStopSignConfig(true);
        if (typeof window.toggleDiceConfig === 'function') window.toggleDiceConfig(true);
        if (typeof window.toggleWheelConfig === 'function') window.toggleWheelConfig(true);
      }

      const signLengths = {};
      for (let i = 1; i <= 4; i++) {
        const sz = Math.random() < 0.5 ? 4 : 2;
        const szEl = document.getElementById(`optSigns${i}Size`);
        if (szEl) szEl.value = String(sz);
        signLengths[i] = (sz === 4) ? 5 : 3;
      }

      const finishPegHole = clashTarget;
      const maxFwdJumpDestHole = clashTarget - 11;
      const maxJumpDestHole = clashTarget - 1;
      const maxNplHole = clashTarget - 11;
      const maxJumpHole = clashTarget - 11;

      // A. Signs Blocks
      for (let i = 1; i <= signsCount; i++) {
        const len = signLengths[i];
        const candidates = shuffle(getMultiples(10, maxClashHole)).filter(h => isFree(h, len));
        if (candidates.length > 0) {
          const start = candidates[0];
          occupy(start, len);
          const sel = document.getElementById(`optSigns${i}Hole`);
          if (sel) { sel.value = String(start); sel.dataset.customized = 'true'; }
        }
      }

      // B. No Passing Lanes (length 5)
      for (let i = 1; i <= nplCount; i++) {
        const candidates = shuffle(getMultiples(10, maxNplHole)).filter(h => isFree(h, 5));
        if (candidates.length > 0) {
          const start = candidates[0];
          occupy(start, 5);
          const sel = document.getElementById(`optNoPassingLane${i}Start`);
          if (sel) { sel.value = String(start); sel.dataset.customized = 'true'; }
        }
      }

      // C. Jumps
      function placeJump(jumpIndex) {
        const preferForward = Math.random() < 0.5;
        const allStarts = shuffle(getMultiples(5, maxJumpHole));
        const directions = preferForward ? [true, false] : [false, true];

        for (const isFwd of directions) {
          const triggerLen = isFwd ? 1 : 3;
          for (const start of allStarts) {
            if (!isFree(start, triggerLen)) continue;
            let destCandidates = [];
            if (isFwd) {
              const minDest = start + 5;
              const maxDest = Math.min(maxFwdJumpDestHole, start + 50);
              for (let d = minDest; d <= maxDest; d += 5) {
                if (isFree(d, 1) && (finishPegHole - d > 10)) destCandidates.push(d);
              }
            } else {
              const minDest = Math.max(5, start - 50);
              const maxDest = start - 5;
              for (let d = minDest; d <= maxDest; d += 5) {
                if (isFree(d, 1) && (d < start || d > start + 2)) destCandidates.push(d);
              }
            }

            if (destCandidates.length > 0) {
              destCandidates = shuffle(destCandidates);
              const dest = destCandidates[0];
              occupy(start, triggerLen);
              occupy(dest, 1);
              const sEl = document.getElementById(`optJump${jumpIndex}Start`);
              if (sEl) { sEl.value = String(start); sEl.dataset.customized = 'true'; }
              const dEl = document.getElementById(`optJump${jumpIndex}Dest`);
              if (dEl) { dEl.value = String(dest); dEl.dataset.customized = 'true'; }
              return true;
            }
          }
        }
        return false;
      }

      for (let i = 1; i <= jumpCount; i++) {
        placeJump(i);
      }

      // D. Single-hole traps
      const singleObstacles = [
        { prefix: 'optJail', suffix: 'Hole', count: jailCount },
        { prefix: 'optBar', suffix: 'Hole', count: barCount },
        { prefix: 'optTimeout', suffix: 'Hole', count: timeoutCount },
        { prefix: 'optDetour', suffix: 'Hole', count: detourCount },
        { prefix: 'optRoundAbout', suffix: 'Hole', count: roundAboutCount },
        { prefix: 'optStopSign', suffix: 'Hole', count: stopSignCount },
        { prefix: 'optDice', suffix: 'Hole', count: diceCount },
        { prefix: 'optWheel', suffix: 'Hole', count: wheelCount }
      ];

      for (const obs of singleObstacles) {
        for (let i = 1; i <= obs.count; i++) {
          const candidates = shuffle(getMultiples(10, maxClashHole)).filter(h => isFree(h, 1));
          if (candidates.length > 0) {
            const h = candidates[0];
            occupy(h, 1);
            const sel = document.getElementById(`${obs.prefix}${i}${obs.suffix}`);
            if (sel) { sel.value = String(h); sel.dataset.customized = 'true'; }
          }
        }
      }

      // Update badges and buttons
      if (typeof window !== 'undefined') {
        for (let i = 1; i <= 4; i++) {
          if (typeof window.onNoPassingLaneFieldChange === 'function') window.onNoPassingLaneFieldChange(i, true);
          if (typeof window.onSignsFieldChange === 'function') window.onSignsFieldChange(i, true);
          if (typeof window.onDiceFieldChange === 'function') window.onDiceFieldChange(i, true);
        }
        for (let i = 1; i <= 8; i++) {
          if (typeof window.onJumpFieldChange === 'function') window.onJumpFieldChange(i, true);
          if (typeof window.onStopSignFieldChange === 'function') window.onStopSignFieldChange(i, true);
        }
        for (let i = 1; i <= 2; i++) {
          if (typeof window.onJailFieldChange === 'function') window.onJailFieldChange(i, true);
          if (typeof window.onBarFieldChange === 'function') window.onBarFieldChange(i, true);
          if (typeof window.onTimeoutFieldChange === 'function') window.onTimeoutFieldChange(i, true);
          if (typeof window.onDetourFieldChange === 'function') window.onDetourFieldChange(i, true);
          if (typeof window.onRoundAboutFieldChange === 'function') window.onRoundAboutFieldChange(i, true);
        }
      }
    } finally {
      if (typeof window !== 'undefined') window._isRandomizingClash = false;
    }

    if (typeof window !== 'undefined') {
      if (typeof window.enforceClashNoOverlap === 'function') window.enforceClashNoOverlap();
      ClashPlacementModal.syncAllPlacementButtons();
      if (typeof window.readFormSettings === 'function' && typeof window.saveSettingsStore === 'function') {
        window.saveSettingsStore(window.readFormSettings());
      }
      if (typeof window.showToast === 'function') {
        if (isFull) {
          window.showToast('⚡ Full Random: Enabled all options with non-overlapping placements!');
        } else {
          window.showToast('🎲 Randomized Cribbage Clash options with zero overlaps!');
        }
      }
    }
  }

  static uncheckClashOptions() {
    const prevSuppress = (typeof window !== 'undefined') ? window._suppressSettingsSave : false;
    if (typeof window !== 'undefined') window._suppressSettingsSave = true;
    try {
      const clashIds = [
        'optNoPassingLanes', 'optJump', 'optJail', 'optBar',
        'optTimeout', 'optDetour', 'optRoundAbout', 'optSigns',
        'optStopSign', 'optDice', 'optWheel', 'optSkunkMarkers'
      ];
      if (typeof document !== 'undefined') {
        const bClash = document.querySelectorAll('#cribbage-clash-options input[type=checkbox]');
        bClash.forEach(element => { element.checked = false; });
        clashIds.forEach(id => {
          const el = document.getElementById(id);
          if (el) el.checked = false;
        });
      }
      if (typeof window !== 'undefined' && window.settings) {
        clashIds.forEach(id => {
          window.settings[id] = false;
          const shortKey = id.replace(/^opt/, '').charAt(0).toLowerCase() + id.replace(/^opt/, '').slice(1);
          window.settings[shortKey] = false;
        });
        window.settings.noPassingLaneStarts = [];
        window.settings.noPassingLaneHoles = [];
        window.settings.jumpStarts = [];
        window.settings.jumpDests = [];
        window.settings.jailHoles = [];
        window.settings.barHoles = [];
        window.settings.timeoutHoles = [];
        window.settings.detourHoles = [];
        window.settings.roundAboutHoles = [];
        window.settings.signsBlocks = [];
        window.settings.stopSignHoles = [];
        window.settings.diceHoles = [];
        window.settings.wheelHoles = [];
      }
      if (typeof window !== 'undefined') {
        if (typeof window.toggleNoPassingLanesConfig === 'function') window.toggleNoPassingLanesConfig(false);
        if (typeof window.toggleJumpConfig === 'function') window.toggleJumpConfig(false);
        if (typeof window.toggleJailConfig === 'function') window.toggleJailConfig(false);
        if (typeof window.toggleBarConfig === 'function') window.toggleBarConfig(false);
        if (typeof window.toggleTimeoutConfig === 'function') window.toggleTimeoutConfig(false);
        if (typeof window.toggleDetourConfig === 'function') window.toggleDetourConfig(false);
        if (typeof window.toggleRoundAboutConfig === 'function') window.toggleRoundAboutConfig(false);
        if (typeof window.toggleSignsConfig === 'function') window.toggleSignsConfig(false);
        if (typeof window.toggleStopSignConfig === 'function') window.toggleStopSignConfig(false);
        if (typeof window.toggleDiceConfig === 'function') window.toggleDiceConfig(false);
        if (typeof window.toggleWheelConfig === 'function') window.toggleWheelConfig(false);
        if (typeof window.toggleSkunkMarkersConfig === 'function') window.toggleSkunkMarkersConfig(false);
      }
      ClashPlacementModal.syncAllPlacementButtons();
    } finally {
      if (typeof window !== 'undefined') window._suppressSettingsSave = prevSuppress;
    }
  }

  static uncheckPintsOptions() {
    const prevSuppress = (typeof window !== 'undefined') ? window._suppressSettingsSave : false;
    if (typeof window !== 'undefined') window._suppressSettingsSave = true;
    try {
      const pintsIds = [
        'optYouDrink', 'optGuysDrink', 'optGalsDrink', 'optLeftDrink',
        'optRightDrink', 'optEveryoneDrink', 'optEveryoneButYouDrink',
        'optPickWhoDrinks'
      ];
      if (typeof document !== 'undefined') {
        const bPints = document.querySelectorAll('#pints-options input[type=checkbox]');
        bPints.forEach(element => { element.checked = false; });
        pintsIds.forEach(id => {
          const el = document.getElementById(id);
          if (el) el.checked = false;
        });
      }
      if (typeof window !== 'undefined' && window.settings) {
        pintsIds.forEach(id => {
          window.settings[id] = false;
          const shortKey = id.replace(/^opt/, '').charAt(0).toLowerCase() + id.replace(/^opt/, '').slice(1);
          window.settings[shortKey] = false;
          window.settings[shortKey + 'Count'] = 0;
          window.settings[shortKey + 'Holes'] = [];
          window.settings[shortKey + 'Pegs'] = 0;
        });
      }
      if (typeof window !== 'undefined') {
        if (typeof window.toggleYouDrinkConfig === 'function') window.toggleYouDrinkConfig(false);
        if (typeof window.toggleGuysDrinkConfig === 'function') window.toggleGuysDrinkConfig(false);
        if (typeof window.toggleGalsDrinkConfig === 'function') window.toggleGalsDrinkConfig(false);
        if (typeof window.toggleLeftDrinkConfig === 'function') window.toggleLeftDrinkConfig(false);
        if (typeof window.toggleRightDrinkConfig === 'function') window.toggleRightDrinkConfig(false);
        if (typeof window.toggleEveryoneDrinkConfig === 'function') window.toggleEveryoneDrinkConfig(false);
        if (typeof window.toggleEveryoneButYouDrinkConfig === 'function') window.toggleEveryoneButYouDrinkConfig(false);
        if (typeof window.togglePickWhoDrinksConfig === 'function') window.togglePickWhoDrinksConfig(false);
      }
      ClashPlacementModal.syncAllPlacementButtons();
    } finally {
      if (typeof window !== 'undefined') window._suppressSettingsSave = prevSuppress;
    }
  }

  static uncheckAdultOptions() {
    const prevSuppress = (typeof window !== 'undefined') ? window._suppressSettingsSave : false;
    if (typeof window !== 'undefined') window._suppressSettingsSave = true;
    try {
      const adultIds = [
        'optLoseClothing', 'optGainClothing', 'optPickWhoLosesClothing',
        'optRightLosesClothing', 'optLeftLosesClothing', 'optRemoveClothingChoice',
        'optTouchBreast', 'optLickBreast', 'optSuckBreast', 'optTouchGenitals',
        'optLickGenitals', 'optSuckGenitals', 'optFingerGenitals',
        'optGenitalIntercourse', 'optTouchAnus', 'optLickAnus',
        'optFingerAnus', 'optAnalIntercourse'
      ];
      if (typeof document !== 'undefined') {
        const bAdult = document.querySelectorAll('#adult-options input[type=checkbox]');
        bAdult.forEach(element => { element.checked = false; });
        adultIds.forEach(id => {
          const el = document.getElementById(id);
          if (el) el.checked = false;
        });
      }
      if (typeof window !== 'undefined' && window.settings) {
        adultIds.forEach(id => {
          window.settings[id] = false;
          const shortKey = id.replace(/^opt/, '').charAt(0).toLowerCase() + id.replace(/^opt/, '').slice(1);
          window.settings[shortKey] = false;
          window.settings[shortKey + 'Count'] = 0;
          window.settings[shortKey + 'Holes'] = [];
          window.settings[shortKey + 'Pegs'] = 0;
        });
      }
      ClashPlacementModal.syncAllPlacementButtons();
    } finally {
      if (typeof window !== 'undefined') window._suppressSettingsSave = prevSuppress;
    }
  }

  static uncheckCrazyOptions() {
    const crazyIds = [
      'optJokers', 'optCutBeforeDiscard', 'opt31sFor5', 'opt19sFor2',
      'optRoundAroundTheWorld', 'optMultipleRunsInPegging', 'optAllRed', 'optAllBlack',
      'optAllOdd', 'optAllEven', 'optAllDifferentSuits',
      'optKH', 'optQS', 'optAS', 'optStraight', 'optFullHouse', 'optStraightFlush',
      'optRoyalFlush', 'optFlushInPlay', 'optPegThroughZones', 'optLast5PegOut',
      'optBunkers', 'optStinkHole', 'optStolenCrib', 'optZeroHand',
      'optZeroCrib', 'optRockCrib', 'optPickYourCrib', 'optSpeedoCribbage', 'optShotgunCribbage', 'optTwistedCribbage', 'optChutesAndLadders', 'optSpades', 'optGuessStarter', 'optFivesCribbage', 'optSevensAndEightsCribbage', 'optDrawCribbage', 'opt7CardCribbage', 'optProgressiveCut', 'optProgressiveSteals'
    ];
    if (typeof document !== 'undefined') {
      crazyIds.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.checked = false;
      });
      const checkAll = document.getElementById('checkAllBonuses');
      if (checkAll) checkAll.checked = false;
    }
    if (typeof window !== 'undefined' && window.settings) {
      crazyIds.forEach(id => {
        window.settings[id] = false;
        const shortKey = id.replace(/^opt/, '').charAt(0).toLowerCase() + id.replace(/^opt/, '').slice(1);
        window.settings[shortKey] = false;
      });
      window.settings.jokers = false;
    }
  }
}

